import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";

function toDisplayMovementType(type) {
  return String(type || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const BADGE_CLASSES = {
  Receipt: "bg-emerald-50 text-emerald-700 border-emerald-200/40",
  "Purchase from Vendor": "bg-emerald-50 text-emerald-700 border-emerald-200/40",
  Return: "bg-blue-50 text-blue-700 border-blue-200/40",
  "Return from Customer": "bg-blue-50 text-blue-700 border-blue-200/40",
  "OEM Warranty Replacement from Vendor":
    "bg-teal-50 text-teal-700 border-teal-200/40",
  "Defective Replacement from Vendor":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "Transfer In": "bg-cyan-50 text-cyan-700 border-cyan-200/40",
  "Transfer Out": "bg-orange-50 text-orange-700 border-orange-200/40",
  Reservation: "bg-purple-50 text-purple-700 border-purple-200/40",
  Outward: "bg-rose-50 text-rose-700 border-rose-200/40",
  Revocation: "bg-gray-50 text-gray-700 border-gray-250",
  "Defect Adjustment": "bg-amber-50 text-amber-700 border-amber-200/40",
  "Excess Return to Vendor": "bg-blue-50 text-blue-700 border-blue-200/40",
  "Excess Return To Vendor": "bg-blue-50 text-blue-700 border-blue-200/40",
  "Defective Replacement Request to Vendor":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "Defective Replacement Request To Vendor":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "OEM Warranty Replacement Request to Vendor":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "OEM Warranty Replacement Request To Vendor":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "Customer Demo Inward": "bg-cyan-50 text-cyan-700 border-cyan-200/40",
  "Customer Demo Outward": "bg-orange-50 text-orange-700 border-orange-200/40",
  "Material On Loan To Customer": "bg-violet-50 text-violet-700 border-violet-200/40",
  "Material On Loan To Vendor": "bg-violet-50 text-violet-700 border-violet-200/40",
  "Advance Replacement To Customer": "bg-orange-50 text-orange-700 border-orange-200/40",
  "Warehouse To Warehouse": "bg-cyan-50 text-cyan-700 border-cyan-200/40",
  // Legacy journal labels
  "OEM Warranty Replacement": "bg-teal-50 text-teal-700 border-teal-200/40",
  "Return to Vendor Excess Return": "bg-blue-50 text-blue-700 border-blue-200/40",
  "Return to Vendor Defective Replacement":
    "bg-amber-50 text-amber-700 border-amber-200/40",
  "Return to Vendor Warranty Replacement":
    "bg-amber-50 text-amber-700 border-amber-200/40",
};

const MOVEMENT_TYPE_KEYS = {
  Receipt: "inventory.movementPurchaseFromVendor",
  "Purchase from Vendor": "inventory.movementPurchaseFromVendor",
  Return: "inventory.movementReturnFromCustomer",
  "Return from Customer": "inventory.movementReturnFromCustomer",
  "OEM Warranty Replacement from Vendor":
    "inventory.movementOemWarrantyReplacementFromVendor",
  "Defective Replacement from Vendor":
    "inventory.movementDefectiveReplacementFromVendor",
  "Transfer In": "inventory.movementTransferIn",
  "Transfer Out": "inventory.movementTransferOut",
  Reservation: "inventory.movementReservation",
  Outward: "inventory.movementOutward",
  Revocation: "inventory.movementRevocation",
  "Defect Adjustment": "inventory.movementDefectAdjustment",
  "Excess Return to Vendor": "inventory.movementReturnToVendorExcessReturn",
  "Excess Return To Vendor": "inventory.movementReturnToVendorExcessReturn",
  "Defective Replacement Request to Vendor":
    "inventory.movementReturnToVendorDefectiveReplacement",
  "Defective Replacement Request To Vendor":
    "inventory.movementReturnToVendorDefectiveReplacement",
  "OEM Warranty Replacement Request to Vendor":
    "inventory.movementReturnToVendorWarrantyReplacement",
  "OEM Warranty Replacement Request To Vendor":
    "inventory.movementReturnToVendorWarrantyReplacement",
  "Customer Demo Inward": "inventory.movementCustomerDemoInward",
  "Customer Demo Outward": "inventory.movementCustomerDemoOutward",
  "Material On Loan To Customer": "inventory.movementMaterialOnLoanToCustomer",
  "Material On Loan To Vendor": "inventory.movementMaterialOnLoanToVendor",
  "Advance Replacement To Customer": "inventory.movementAdvanceReplacementToCustomer",
  "Warehouse To Warehouse": "inventory.movementWarehouseToWarehouse",
  // Legacy journal labels
  "OEM Warranty Replacement":
    "inventory.movementReturnToVendorWarrantyReplacement",
  "Return to Vendor Excess Return": "inventory.movementReturnToVendorExcessReturn",
  "Return to Vendor Defective Replacement":
    "inventory.movementReturnToVendorDefectiveReplacement",
  "Return to Vendor Warranty Replacement":
    "inventory.movementReturnToVendorWarrantyReplacement",
};

export function MovementTypeBadge({ type }) {
  const { t } = useTranslation();
  const displayType = toDisplayMovementType(type);
  const labelKey = MOVEMENT_TYPE_KEYS[displayType];

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border whitespace-nowrap",
        BADGE_CLASSES[displayType] || "bg-gray-50 text-gray-700 border-gray-200"
      )}
    >
      {labelKey ? t(labelKey) : displayType || "—"}
    </span>
  );
}
